package org.gymly.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.PortalRepo
import org.gymly.model.PortalSchedule
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.util.WeekRange
import androidx.compose.foundation.clickable
import androidx.compose.ui.Alignment
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.MobilePageHeader
import org.gymly.util.formatTime
import org.gymly.util.userMessage
import org.gymly.util.weekdayLongEn

@Composable
fun SchedulesScreen(
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onLangEs: (Boolean) -> Unit = {},
    onBell: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<PortalSchedule>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var bookingId by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    // Móvil: semana (0 = esta) y día seleccionado (1 = lunes … 5 = viernes).
    var weekOffset by remember { mutableStateOf(0) }
    var mobileDay by remember { mutableStateOf(1) }
    val week = remember(weekOffset) { WeekRange.ofOffset(weekOffset) }

    fun load() {
        scope.launch {
            error = null
            try {
                rows = PortalRepo.api.fetchSchedulesWeek(WeekRange.ofOffset(weekOffset).mondayIso)
            } catch (e: Exception) {
                error = e.userMessage("Error al cargar horarios.")
            } finally {
                loading = false
            }
        }
    }

    fun book(item: PortalSchedule) {
        if (!item.bookable || item.full || item.alreadyBooked || bookingId != null) return
        scope.launch {
            bookingId = item.id
            message = null
            try {
                PortalRepo.api.createBooking(item.id, item.nextSessionDate, null)
                message = "Reserva confirmada para ${item.nextSessionDate ?: "la próxima sesión"}."
                load()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo reservar.")
            } finally {
                bookingId = null
            }
        }
    }

    LaunchedEffect(weekOffset) { load() }

    if (loading && rows.isEmpty()) {
        GymlyLoadingCenter()
        return
    }

    val subtitle = "Clases semanales abiertas para reservar."

    BoxWithConstraints(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
        val columns = when {
            mobile -> 1
            maxWidth < 600.dp -> 1
            else -> 2
        }
        Column(Modifier.fillMaxSize()) {
            if (mobile) {
                MobilePageHeader(
                    title = "Horarios",
                    subtitle = subtitle,
                    unreadCount = unreadCount,
                    langEs = langEs,
                    onLangEs = onLangEs,
                    onBell = onBell,
                )
            }

            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(GymlySpace.lg),
            ) {
                if (!mobile) {
                    Text("Horarios", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(6.dp))
                    Text(subtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                    Spacer(Modifier.height(16.dp))
                }
                error?.let {
                    Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = 10.dp))
                }
                message?.let {
                    Text(it, color = GymlyColors.Success, modifier = Modifier.padding(bottom = 10.dp))
                }
                if (mobile) {
                    // Solo la semana actual: sin semanas anteriores ni adelantadas.
                    Text(
                        week.labelEs(),
                        fontSize = 13.sp,
                        color = GymlyColors.TextMuted,
                    )
                    Spacer(Modifier.height(10.dp))
                    DayChipsRow(
                        week = week,
                        selected = mobileDay,
                        onSelect = { mobileDay = it },
                    )
                    Spacer(Modifier.height(12.dp))
                }
                val visibleRows = if (mobile) rows.filter { it.weekday == mobileDay } else rows
                if (visibleRows.isEmpty()) {
                    Text(
                        if (mobile) "Sin franjas este día." else "No hay franjas publicadas entre semana.",
                        color = GymlyColors.TextMuted,
                    )
                } else {
                    // Un entrenador con varios horarios: se muestra una vez, con todos sus días y horas.
                    val trainerGroups = visibleRows.groupBy { it.trainerName ?: it.title }.entries.toList()
                    trainerGroups.chunked(columns).forEach { rowGroups ->
                        Row(
                            Modifier.fillMaxWidth().padding(bottom = 12.dp),
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            rowGroups.forEach { group ->
                                TrainerScheduleCard(
                                    name = group.key,
                                    items = group.value,
                                    modifier = Modifier.weight(1f),
                                )
                            }
                            repeat(columns - rowGroups.size) {
                                Spacer(Modifier.weight(1f))
                            }
                        }
                    }
                }
                Spacer(Modifier.height(GymlySpace.xl))
            }
        }
    }
}

private val WEEKDAY_NAMES_ES = mapOf(
    1 to "Lunes", 2 to "Martes", 3 to "Miércoles", 4 to "Jueves", 5 to "Viernes", 6 to "Sábado", 7 to "Domingo",
)

/** Un entrenador: nombre una vez y debajo cada día con sus franjas (formato del inicio). */
@Composable
private fun TrainerScheduleCard(name: String, items: List<PortalSchedule>, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .border(1.dp, GymlyColors.Primary.copy(alpha = 0.25f), RoundedCornerShape(14.dp))
            .background(GymlyColors.Primary.copy(alpha = 0.07f))
            .padding(horizontal = 16.dp, vertical = 14.dp),
    ) {
        Text(name, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = GymlyColors.Primary, maxLines = 1)
        Spacer(Modifier.height(8.dp))
        items.groupBy { it.weekday }.toList().sortedBy { it.first }.forEach { (weekday, dayItems) ->
            val hours = dayItems.sortedBy { it.startTime }
                .joinToString(" · ") { "${it.startTime.take(5)} – ${it.endTime.take(5)}" }
            Text(
                "${WEEKDAY_NAMES_ES[weekday] ?: ""}: $hours",
                color = GymlyColors.TextMuted,
                fontSize = 13.sp,
            )
            Spacer(Modifier.height(4.dp))
        }
        Spacer(Modifier.height(6.dp))
        Text("Horario de cobertura · sin reserva", color = GymlyColors.TextMuted, fontSize = 12.sp)
    }
}

/** Móvil: un día a la vez; chips Lun–Vie con su fecha, como en el admin. */
@Composable
private fun DayChipsRow(week: WeekRange, selected: Int, onSelect: (Int) -> Unit) {
    val names = listOf("Lun", "Mar", "Mié", "Jue", "Vie")
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        names.forEachIndexed { index, name ->
            val day = index + 1
            val on = day == selected
            val iso = week.dateForWeekday(day)
            Column(
                Modifier
                    .weight(1f)
                    .clip(RoundedCornerShape(10.dp))
                    .background(if (on) AdminColors.BrandPanel else AdminColors.SurfaceContainer)
                    .border(1.dp, AdminColors.BrandPanel.copy(alpha = 0.4f), RoundedCornerShape(10.dp))
                    .clickable { onSelect(day) }
                    .padding(vertical = 8.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    name,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 13.sp,
                    color = if (on) AdminColors.OnBrand else AdminColors.Text,
                )
                Text(
                    "${iso.substring(8, 10)}/${iso.substring(5, 7)}",
                    fontSize = 11.sp,
                    color = if (on) AdminColors.OnBrand else AdminColors.TextMuted,
                )
            }
        }
    }
}

@Composable
private fun ScheduleClassCard(
    item: PortalSchedule,
    mobile: Boolean,
    booking: Boolean,
    onBook: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val day = weekdayLongEn[item.weekday] ?: item.weekday.toString()
    val timeRange = "${formatTime(item.startTime)}-${formatTime(item.endTime)}"
    val trainer = item.trainerName?.takeIf { it.isNotBlank() }
    // Regla del portal (móvil y escritorio): solo el entrenador, sin cupos. Plan diario: sin entrenador.
    val metaLine = trainer.orEmpty()

    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(18.dp),
    ) {
        if (mobile) {
            Text("$day · $timeRange", color = GymlyColors.TextMuted, fontSize = 13.sp)
            Spacer(Modifier.height(6.dp))
            Text(
                item.title,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = GymlyColors.Text,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                metaLine,
                color = if (item.full) Color(0xFFC62828) else GymlyColors.TextMuted,
                fontSize = 13.sp,
            )
            Spacer(Modifier.height(16.dp))
            val enabled = item.bookable && !item.full && !item.alreadyBooked && !booking
            Button(
                onClick = onBook,
                enabled = enabled,
                colors = ButtonDefaults.buttonColors(
                    containerColor = GymlyColors.Primary,
                    disabledContainerColor = GymlyColors.Outline.copy(alpha = 0.35f),
                ),
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth(),
            ) {
                if (booking) {
                    CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                } else {
                    Text(
                        when {
                            !item.bookable -> "Sin reserva"
                            item.alreadyBooked -> "Ya reservado"
                            item.full -> "Sin cupos"
                            else -> "Reservar"
                        },
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        } else {
            val instructorLine = listOfNotNull(
                trainer,
                item.shiftLabel,
            ).joinToString(" - ").ifBlank { "—" }

            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(
                    item.title,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = GymlyColors.Text,
                    modifier = Modifier.weight(1f).padding(end = 8.dp),
                )
                if (item.full) {
                    Text("Lleno", color = Color(0xFFC62828), fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                } else {
                    Text(
                        "Cupos disponibles: ${item.availableSpots}",
                        color = GymlyColors.Text,
                        fontSize = 13.sp,
                    )
                }
            }
            Spacer(Modifier.height(8.dp))
            Text("$day · $timeRange", color = GymlyColors.TextMuted, fontSize = 13.sp)
            Spacer(Modifier.height(4.dp))
            Text(instructorLine, color = GymlyColors.TextMuted, fontSize = 13.sp)
            Spacer(Modifier.height(16.dp))
            val enabled = item.bookable && !item.full && !item.alreadyBooked && !booking
            Button(
                onClick = onBook,
                enabled = enabled,
                colors = ButtonDefaults.buttonColors(
                    containerColor = GymlyColors.Primary,
                    disabledContainerColor = GymlyColors.Outline.copy(alpha = 0.35f),
                ),
                shape = RoundedCornerShape(10.dp),
            ) {
                if (booking) {
                    CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                } else {
                    Text(
                        when {
                            !item.bookable -> "Sin reserva"
                            item.alreadyBooked -> "Ya reservado"
                            item.full -> "Sin cupos"
                            else -> "Reservar"
                        },
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
    }
}
